import { useState, useCallback } from 'react'
import { api } from '~/services/api'
import { DetailedProposal, VoteRequest } from '~/types/api'
import { useAccount } from './useAccount'

export function useProposalDetailsByRepository({ 
  repositoryName, 
  issueNumber 
}: { 
  repositoryName: string
  issueNumber: number 
}) {
  const [proposal, setProposal] = useState<DetailedProposal | null>(null)
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<Error | null>(null)
  const [isVoting, setIsVoting] = useState(false)
  const { isAuthenticated } = useAccount()

  const fetchProposal = useCallback(async () => {
    try {
      setIsLoading(true)
      const data = await api.proposals.getByRepositoryAndIssue(repositoryName, issueNumber)
      setProposal(data)
      setError(null)
    } catch (err) {
      console.error('Failed to fetch proposal:', err)
      setError(err instanceof Error ? err : new Error('Failed to fetch proposal'))
    } finally {
      setIsLoading(false)
    }
  }, [repositoryName, issueNumber])

  const vote = useCallback(async (voteType: 'approve' | 'delete', voteDirection: 'for' | 'against') => {
    if (!isAuthenticated || !proposal) {
      throw new Error('Please connect your wallet to vote')
    }

    try {
      setIsVoting(true)
      const voteData: VoteRequest = {
        voteType,
        voteDirection,
      }
      const result = await api.proposals.vote(proposal.proposalId, voteData)
      
      // Update local state with new vote counts
      setProposal({
        ...proposal,
        approveVotesFor: result.proposal.approveVotesFor,
        approveVotesAgainst: result.proposal.approveVotesAgainst,
        deleteVotesFor: result.proposal.deleteVotesFor,
        deleteVotesAgainst: result.proposal.deleteVotesAgainst,
        status: result.proposal.status,
      })
      
      return result
    } catch (err) {
      console.error('Failed to vote:', err)
      throw err
    } finally {
      setIsVoting(false)
    }
  }, [proposal, isAuthenticated])

  return {
    proposal,
    isLoading,
    error,
    isVoting,
    fetchProposal,
    vote,
  }
} 